import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useSentryLogger } from '@App/hooks/log';
import { ApplicationFinalStage } from '@App/interfaces/application-final-stage';
import { ApplicationStage } from '@App/interfaces/application-stage.enum';
import { poll } from '@Shared/helpers/poll';
import { DecisionStages } from '@Shared/types';
import { WaitViewLoader } from '@Views/wait/WaitViewLoader';
import { NavigationState } from '@src/app/interfaces/debtcon';
import ErrorView from '@src/ui/components/ErrorView/ErrorView';

export interface WaitViewProps {
    setOffers?: Function;
    setApplicationStage?: Function;
    applicationId: string;
    fetchApplication?: () => Promise<any>;
    runDecisioning?: () => Promise<any>;
    runCreditCommitments?: () => Promise<any>;
    saveFinalStage: (
        stageToSave: ApplicationFinalStage,
        applicationStage: ApplicationStage,
    ) => Promise<any>;
}

const validatePollResult = (data: any) => {
    if (data.success) {
        const status = data.status;
        return (
            status.includes(ApplicationStage.DECISIONING_ERROR) ||
            status.includes(ApplicationStage.DECISIONING_DECLINE) ||
            status.includes(ApplicationStage.DECISIONING_ACCEPT) ||
            status.includes(ApplicationStage.CLOSED)
        );
    }
    return false;
};

const WaitView = ({
    setOffers,
    setApplicationStage,
    applicationId,
    fetchApplication,
    runDecisioning,
    runCreditCommitments,
    saveFinalStage,
}: WaitViewProps) => {
    const navigate = useNavigate();

    const storeId = useMemo(
        () => `appState - ${applicationId}`,
        [applicationId],
    );
    const prevDecisionStage = useRef(
        localStorage?.getItem(storeId) || DecisionStages.COMMITMENT,
    );
    const [currentDecisionStage, setCurrentDecisionStage] = useState(
        localStorage?.getItem(storeId) || DecisionStages.COMMITMENT,
    );

    const componentRef = useRef(false); // to avoid double fetch (Component was mounted)
    const [error, setError] = useState<boolean>(false);
    const { sentryLog, sentryError, flushLogs } = useSentryLogger(
        applicationId || '',
        'wait view',
    );

    const location = useLocation();
    const state = location.state as NavigationState;

    const { debtCon } = state || {};

    const setLocalStorage = useCallback(
        (status: DecisionStages) => {
            localStorage?.setItem(storeId, status + '');
        },
        [storeId],
    );

    const setCurrentAppState = useCallback(
        (status: DecisionStages, callBack?: Function) => {
            setCurrentDecisionStage(() => {
                setLocalStorage(status);
                if (callBack) callBack();
                return status;
            });
        },
        [setLocalStorage],
    );

    const onCommitmentSuccess = useCallback(
        (response: any) => {
            if (!response.success) {
                setCurrentAppState(DecisionStages.COMMITMENT, () =>
                    sentryError(
                        'error on commitment',
                        response?.error?.response || response?.error,
                        () => {
                            setError(true);
                            flushLogs();
                        },
                    ),
                );
                return;
            }

            setCurrentAppState(DecisionStages.DECISION, () =>
                sentryLog('creditCommitment was successful'),
            );
        },
        [flushLogs, sentryError, sentryLog, setCurrentAppState],
    );

    const onDecisionSuccess = useCallback(
        (response: any) => {
            if (!response.success) {
                setCurrentAppState(DecisionStages.DECISION, () =>
                    sentryError(
                        'error on decision',
                        response?.error?.response || response.error,
                        () => {
                            setError(true);
                            flushLogs();
                        },
                    ),
                );
                return;
            }
            setCurrentAppState(DecisionStages.POLL, () =>
                sentryLog('decision was requested'),
            );
        },
        [flushLogs, sentryError, sentryLog, setCurrentAppState],
    );

    const onPollSuccess = useCallback(
        (response: any) => {
            const goToOffer = async () => {
                await saveFinalStage(
                    ApplicationFinalStage.SQL,
                    ApplicationStage.DECISIONING_ACCEPT,
                );

                navigate('/eligibility/offer/approval', { replace: true });
            };

            const goToReject = async () => {
                await saveFinalStage(
                    ApplicationFinalStage.CLOSED,
                    ApplicationStage.DECISIONING_DECLINE,
                );

                navigate('/eligibility/reject', { replace: true });
            };

            if (!response.success) {
                setCurrentAppState(DecisionStages.POLL, () =>
                    sentryError(
                        'error on poll',
                        response?.error?.response || response.error,
                        () => {
                            flushLogs();
                            setError(true);
                        },
                    ),
                );
                return;
            }

            const decisioningAccept = response.status.includes(
                ApplicationStage.DECISIONING_ACCEPT,
            );
            if (!setOffers || !setApplicationStage) return;
            window.onbeforeunload = null;

            if (decisioningAccept) {
                setOffers(response.offers);
                setApplicationStage(ApplicationStage.DECISIONING_ACCEPT);
                sentryLog('accepted decision', goToOffer);
                flushLogs();
            }
            if (!decisioningAccept) {
                setApplicationStage(ApplicationStage.DECISIONING_DECLINE);
                sentryLog('rejected decision', goToReject);
                flushLogs();
            }
        },
        [
            setOffers,
            setApplicationStage,
            navigate,
            setCurrentAppState,
            sentryError,
            flushLogs,
            sentryLog,
            saveFinalStage,
        ],
    );

    /**
     * Poll application data
     */
    const startPollOffers = useCallback(() => {
        if (!fetchApplication) return;
        sentryLog('poll started');
        poll(fetchApplication, validatePollResult, 5000, 120 * 1000).then(
            onPollSuccess,
        );
    }, [onPollSuccess, fetchApplication, sentryLog]);

    /**
     * Request decision from ms-d2c
     */
    const startDecisioning = useCallback(() => {
        if (!runDecisioning) return;
        sentryLog('decision started');
        runDecisioning().then(onDecisionSuccess);
    }, [runDecisioning, sentryLog, onDecisionSuccess]);

    /**
     * CreditCommitment
     */
    const startUpdateCreditCommitment = useCallback(() => {
        if (!runCreditCommitments) return;
        sentryLog('creditCommitment started');
        runCreditCommitments().then(onCommitmentSuccess);
    }, [runCreditCommitments, onCommitmentSuccess, sentryLog]);

    /**
     * Call fetch depends of app stage
     **/
    const startProcessDependsOnStatus = useCallback(() => {
        if (error) {
            flushLogs(); // empty logs if there is any left
            setError(false);
        }
        const prevState =
            currentDecisionStage ||
            localStorage?.getItem(storeId) ||
            DecisionStages.START;

        if (!debtCon && prevState === DecisionStages.COMMITMENT) {
            startUpdateCreditCommitment();
        }

        if (
            (debtCon && prevState === DecisionStages.COMMITMENT) ||
            prevState === DecisionStages.DECISION
        ) {
            startDecisioning();
        }

        if (
            prevState === DecisionStages.POLL ||
            prevState === DecisionStages.DONE
        ) {
            startPollOffers();
        }
    }, [
        currentDecisionStage,
        error,
        flushLogs,
        startDecisioning,
        startPollOffers,
        startUpdateCreditCommitment,
        storeId,
        debtCon,
    ]);

    // ON MOUNT -------------------------------------------------------------------------------------------
    useEffect(() => {
        if (!error && !componentRef.current && applicationId) {
            componentRef.current = true;
            startProcessDependsOnStatus();

            window.onbeforeunload = (event) => {
                const e = event || window.event;
                sentryLog('page leave requested');
                flushLogs();
                e.preventDefault();
                // Cancel the event
                if (e) {
                    e.returnValue = ''; // Legacy method for cross browser support
                }
                return ''; // Legacy method for cross browser support
            };
        }
    }, [
        error,
        sentryLog,
        startProcessDependsOnStatus,
        currentDecisionStage,
        applicationId,
        flushLogs,
    ]);

    // ON UPDATED APP STAGE
    useEffect(() => {
        if (
            !error &&
            componentRef.current &&
            prevDecisionStage.current !== currentDecisionStage
        ) {
            prevDecisionStage.current = currentDecisionStage;
            startProcessDependsOnStatus();
        }
    }, [currentDecisionStage, error, startProcessDependsOnStatus]);

    return (
        <>
            {!error && <WaitViewLoader />}
            {error && <ErrorView onRetry={startProcessDependsOnStatus} />}
        </>
    );
};

export default WaitView;
